import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import type { Mutation, MutationReportProductArgs } from '@apiTypes';
import {
    Button,
    FormField,
    HStack,
    TextBox,
    TextField,
    VStack,
} from '@library';
import type { FalseDataFormValues, Product } from '../../../types';
import FormAlert from '../../../../core/components/FormAlert';
import submitFalseDataMutation from '../../../queries/submitFalseDataMutation';
import useForm from '../../../../core/hooks/form/useForm';
import useStoreAndLanguageParams from '../../../../core/hooks/useStoreAndLanguageParams';
import { useCurrentUserLoggedInOnly } from '../../../../core/hooks/useCurrentUser';
import { useCurrentCustomerLoggedInOnly } from '../../../../core/hooks/useCurrentCustomer';

const useFalseDataForm = (
    product: Product,
    openSuccessModal: () => void,
    selectedSku: string
) => {
    const { store, language } = useStoreAndLanguageParams();
    const intl = useIntl();
    const [submitRequest] = useMutation<
        {
            reportProduct: Mutation['reportProduct'];
        },
        MutationReportProductArgs
    >(submitFalseDataMutation);

    const currentUser = useCurrentUserLoggedInOnly();
    const currentCustomer = useCurrentCustomerLoggedInOnly();

    const {
        generateFormFieldProps,
        values,
        handleChange,
        isSubmitting,
        handleSubmit,
        errors,
    } = useForm<FalseDataFormValues>({
        initialState: {
            productName: product.name,
            articleNumber: selectedSku,
            subject: `${intl.formatMessage({
                id: 'incorrect data',
            })}: ${selectedSku} ${product.name}`,
            message: '',
        },
        onSubmit: async (): Promise<boolean> => {
            const { data } = await submitRequest({
                variables: {
                    name: currentCustomer.name,
                    customerNumber: currentUser.customerNumber.toString(),
                    emailAddress: currentUser.email,
                    username: currentUser.username,
                    sku: selectedSku || '',
                    productName: values.productName,
                    message: values.message,
                    store,
                    language,
                    url: window.location.href,
                },
            });

            return !!data?.reportProduct;
        },
        onSuccess: () => {
            openSuccessModal();
        },
        validationSchema: {
            message: 'required',
        },
    });

    return {
        errors,
        generateFormFieldProps,
        values,
        handleChange,
        isSubmitting,
        handleSubmit,
    };
};

const FalseDataForm = ({
    onClose,
    openSuccessModal,
    selectedSku = '',
    product,
}: {
    onClose: () => void;
    openSuccessModal: () => void;
    product: Product;
    selectedSku?: string;
}) => {
    const intl = useIntl();

    const { errors, values, handleChange, isSubmitting, handleSubmit } =
        useFalseDataForm(product, openSuccessModal, selectedSku);

    return (
        <VStack gap="small">
            {errors.submissionError && (
                <FormAlert message="your message was not sent." />
            )}
            <VStack gap="small">
                <FormField label={<FormattedMessage id="article name" />}>
                    <TextField value={values.productName} disabled />
                </FormField>
                <FormField label={<FormattedMessage id="article no" />}>
                    <TextField
                        value={
                            values.articleNumber
                                ? values.articleNumber
                                : intl.formatMessage({
                                      id: 'not applicable',
                                  })
                        }
                        disabled
                    />
                </FormField>

                <FormField label={<FormattedMessage id="subject" />}>
                    <TextField
                        value={`${intl.formatMessage({
                            id: 'incorrect data',
                        })}: ${
                            values.articleNumber ? values.articleNumber : ''
                        } ${product.name}`}
                        disabled
                    />
                </FormField>

                <FormField
                    label={<FormattedMessage id="message" />}
                    required
                    error={
                        errors.message &&
                        intl.formatMessage({ id: errors.message })
                    }
                >
                    <TextBox
                        value={values.message}
                        placeholder={intl.formatMessage({
                            id: 'Product ProductDetails FalseDataForm MessagePlaceholder',
                        })}
                        rows={5}
                        onChange={value => {
                            handleChange('message', value);
                        }}
                        error={!!errors.message}
                    />
                </FormField>
            </VStack>
            <HStack gap="small" justify="end">
                <Button secondary onClick={onClose}>
                    <FormattedMessage id="cancel" />
                </Button>

                <Button
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    onClick={handleSubmit}
                >
                    <FormattedMessage id="send message" />
                </Button>
            </HStack>
        </VStack>
    );
};

export default memo(FalseDataForm);
